import { taxFeeTypes } from "common";
import { concat } from "lodash";
import {
    CouponSource,
    LineItem,
    LineItemCode,
    LineItemObject,
    LineItemType,
    LineItemVisibility,
    PaymentMethod,
} from "types";
import { CreatePurchaseItemLineItem } from "@/stores/createPurchaseStore";

export const isStoreCredit = (lineItem: LineItem) => {
    return lineItem.payment?.method === PaymentMethod.STORE_CREDIT;
};

export const isPurchaseLevelGift = (lineItem: LineItem) => {
    return lineItem.payment?.method === PaymentMethod.GIFT;
};

export const isLineItemPurchaseLevel = (lineItem: LineItem) => {
    if (!lineItem) {
        return false;
    }

    const isPaymentCoupon = lineItem?.coupon && lineItem?.type === LineItemType.PAYMENT;

    return (
        lineItem?.type === LineItemType.AFFILIATE_DEPOSIT ||
        isPurchaseLevelGift(lineItem) ||
        isStoreCredit(lineItem) ||
        isPaymentCoupon
    );
};

export const getCodes = (types: LineItemType[], lineItems: LineItem[]) => {
    return lineItems.filter((lineItem: LineItem) => types.includes(lineItem.type));
};

export const AFFILIATE_LINE_ITEM_CODES = [
    LineItemType.AFFILIATE_COMMISSION,
    LineItemType.AFFILIATE_DEPOSIT,
    LineItemType.AFFILIATE_DISCOUNT,
];

/**
 * Get coupon codes from item & purchase line items, checks for coupons in purchase line items since it can be missing for voucher on item level
 * @param itemLineItems
 * @param purchaseLineItems
 * @returns {LineItem[]}
 */
export const getCouponCodes = (itemLineItems: LineItem[], purchaseLineItems: LineItem[]): LineItem[] => {
    const itemCoupons = getCodes([LineItemType.COUPON_DISCOUNT], itemLineItems).filter((lineItem) => {
        // If the coupon is a gift, then no need to show it in coupon codes since we already
        // show the gift code in gift section
        return lineItem.coupon?.source !== CouponSource.GIFT;
    });
    const itemCodes = new Set(itemCoupons.map((coupon) => coupon.code));
    const purchaseCoupons = purchaseLineItems.filter(
        (lineItem) =>
            lineItem.coupon &&
            !lineItem.voidingLineItem &&
            lineItem.type !== LineItemType.REFUND &&
            !itemCodes.has(lineItem.code) &&
            ![PaymentMethod.GIFT, PaymentMethod.STORE_CREDIT].includes(
                (lineItem.payment?.method ?? "") as PaymentMethod,
            ),
    );

    return concat(itemCoupons, purchaseCoupons);
};

export const getMembershipCodes = (itemLineItems: LineItem[]): LineItem[] => {
    return itemLineItems.filter(
        (lineItem) =>
            lineItem.type === LineItemType.DISCOUNT && lineItem.templateCode === LineItemCode.MEMBERSHIP_DISCOUNT,
    );
};

export const getLineItemsByPaymentMethods = (paymentMethods: PaymentMethod[], lineItems: LineItem[]) => {
    return lineItems.filter((lineItem: LineItem) => {
        if (paymentMethods.includes(PaymentMethod.AFFILIATE_DEPOSIT)) {
            // When voucher is applied for affiliate, the method is "voucher" instead
            return (
                (paymentMethods.includes(lineItem.payment?.method as PaymentMethod) ||
                    (lineItem.type === LineItemType.AFFILIATE_DEPOSIT && lineItem.object === LineItemObject.PAYMENT)) &&
                lineItem.visibility !== LineItemVisibility.NONE
            );
        }

        const isMatchingLineItem =
            paymentMethods.includes(lineItem.payment?.method as PaymentMethod) &&
            lineItem.visibility !== LineItemVisibility.NONE;

        if (
            (paymentMethods.includes(PaymentMethod.GIFT) || paymentMethods.includes(PaymentMethod.STORE_CREDIT)) &&
            [PaymentMethod.GIFT, PaymentMethod.STORE_CREDIT].includes(lineItem.payment?.method as PaymentMethod)
        ) {
            // If we are returning Gift lineItems, we need to check and return the gift lineItems with coupon
            // otherwise, incase we refund the gift to storecredit and re-apply it back, those 2 lineitems as well
            // will be having the method as GIFT, but those are payment & refund lineItems and we cannot remove those
            return isMatchingLineItem && !!lineItem.coupon;
        }

        return isMatchingLineItem;
    });
};

export const isTaxFee = (lineItem: LineItem | CreatePurchaseItemLineItem) => {
    if ("type" in lineItem && lineItem.type) {
        return taxFeeTypes.includes(lineItem.type);
    }
};
